/**
 * form-embed D3 — Telify's page for a studio's general form on its website, `/forms/<siteKey>`: the form's
 * name and description over the `site-form` piece in the standard look, inside the studio's header and
 * footer (the custom site's on a custom one). Any form that cannot be read is the ordinary not-found page,
 * the same in every case, as the server answers it.
 */
import { useParams } from 'react-router-dom';

import { SiteFormView } from '../pieces/site-form/SiteFormView';
import { useSiteForm } from '../pieces/site-form/useSiteForm';
import { usePageTitle } from '../ui/usePageTitle';
import css from './SiteFormPage.module.css';
import { NotFoundPage } from './NotFoundPage';

export function SiteFormPage() {
  const { formKey = '' } = useParams<{ formKey: string }>();
  // Each form is its own page: nothing typed into one carries into another.
  return <SiteFormPageFor key={formKey} formKey={formKey} />;
}

function SiteFormPageFor({ formKey }: { readonly formKey: string }) {
  const runtime = useSiteForm({ options: { form: formKey } });
  const { form, state } = runtime;
  usePageTitle(form?.title ?? (state.status === 'unavailable' ? undefined : 'Form'));
  if (state.status === 'unavailable') return <NotFoundPage />;
  return (
    <div className={css.page}>
      {form && state.status !== 'sent' && (
        <header className={css.head}>
          <h1 className={css.name}>{form.title}</h1>
          {form.description && <p className={css.desc}>{form.description}</p>}
        </header>
      )}
      <SiteFormView {...runtime} />
    </div>
  );
}
